import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

// Measured from overlay.jpg (1920x1080). The clip radius sits inside the white circle's edge
// so a measuring error or JPEG blur can never leak logo pixels onto the green.
const CIRCLES = { left: { x: 477, y: 521 }, right: { x: 1441, y: 521 } };
const CLIP_RADIUS = 270;

const OverlayContainer = styled.div`
  text-align: center;
`;

const DownloadButton = styled.button`
  margin-top: 20px;
`;

function Overlay({ overlayImage, logos }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    const loadImages = async () => {
      try {
        const overlayImg = await loadImage(overlayImage);

        // Set canvas dimensions
        canvas.width = overlayImg.width;
        canvas.height = overlayImg.height;

        // Draw the overlay image
        context.drawImage(overlayImg, 0, 0);

        // Desired maximum logo dimensions
        const maxLogoWidth = 400; // Adjust as needed
        const maxLogoHeight = 400; // Adjust as needed

        // Draw logos if they exist, clipped to the circle so square logos can't stick out
        for (const side of ['left', 'right']) {
          if (!logos[side]) continue;
          const logoImg = await loadImage(logos[side]);

          // Calculate scaled dimensions while maintaining aspect ratio
          const { width: logoWidth, height: logoHeight } = getScaledDimensions(
            logoImg,
            maxLogoWidth,
            maxLogoHeight
          );

          const { x, y } = CIRCLES[side];
          context.save();
          context.beginPath();
          context.arc(x, y, CLIP_RADIUS, 0, 2 * Math.PI);
          context.clip();
          context.drawImage(logoImg, x - logoWidth / 2, y - logoHeight / 2, logoWidth, logoHeight);
          context.restore();
        }
      } catch (error) {
        console.error('Error loading images:', error);
      }
    };

    loadImages();

    function loadImage(src) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.src = src;
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
      });
    }
  }, [overlayImage, logos]);

  function getScaledDimensions(img, maxWidth, maxHeight) {
    const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
    return {
      width: img.width * ratio,
      height: img.height * ratio,
    };
  }
  

  const handleDownload = () => {
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.download = 'composed-image.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <OverlayContainer>
      <canvas ref={canvasRef} style={{ maxWidth: '100%', height: 'auto' }} />
      <DownloadButton onClick={handleDownload}>⬇️ Download Image</DownloadButton>
    </OverlayContainer>
  );
}

export default Overlay;
